import type {
  AdminCertificatesKpisEnvironment,
  CertificatesKpisEnvironmentSelectorProps,
} from "../../models/admin-kpis.models";
import { getAdminKpisAriaExpandedProps } from "../../utils/admin-kpis.utils";
import { useClickOutside } from "../../../shared/components/Dropdown/useClickOutside";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

const CertificatesKpisEnvironmentSelector = ({
  value,
  options,
  loading,
  onChange,
}: CertificatesKpisEnvironmentSelectorProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const selectedOption = options.find((environment) => environment.value === value);

  const closeEnvironmentOptions = useCallback(() => {
    setIsOpen(false);
  }, []);

  useClickOutside(closeEnvironmentOptions, containerRef);

  useEffect(() => {
    if (loading) {
      closeEnvironmentOptions();
    }
  }, [closeEnvironmentOptions, loading]);

  return (
    <div ref={containerRef} className="relative w-36 max-w-full">
      <div
        id="admin-kpis-snowflake-environment-label"
        className="mb-1 text-[11px] font-medium text-[#6B7473] dark:text-gray-300"
      >
        Snowflake Environment
      </div>
      <div className="relative">
        <button
          id="admin-kpis-snowflake-environment-trigger"
          type="button"
          disabled={loading}
          data-test="admin-kpis-snowflake-environment-select"
          {...getAdminKpisAriaExpandedProps(isOpen)}
          aria-labelledby="admin-kpis-snowflake-environment-label admin-kpis-snowflake-environment-trigger"
          className="h-9 w-full min-w-0 border border-[#D7E2DE] bg-white px-2 pr-7 text-left text-xs text-gray-900 outline-none transition-colors hover:border-green focus:border-green disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
          onClick={() => {
            if (loading) {
              return;
            }

            setIsOpen((currentIsOpen) => !currentIsOpen);
          }}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              event.preventDefault();
              closeEnvironmentOptions();
            }
          }}
        >
          {selectedOption?.label ?? value}
        </button>
        <ChevronDownIcon
          className={twMerge(
            "pointer-events-none absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 flex-none text-[#4A5655] transition-transform dark:text-gray-200",
            isOpen && "rotate-180",
            loading && "opacity-60",
          )}
        />
      </div>
      {isOpen && (
        <div
          aria-labelledby="admin-kpis-snowflake-environment-label"
          className="absolute left-0 right-0 z-30 mt-1 overflow-hidden border border-[#D7E2DE] bg-white shadow-md dark:border-gray-700 dark:bg-gray-900"
        >
          {options.map((environment) => {
            const isSelected = environment.value === value;

            return (
              <button
                key={environment.value}
                type="button"
                className={twMerge(
                  "flex min-h-8 w-full items-center px-2 py-1.5 text-left text-xs hover:bg-[#E9F4F3] dark:hover:bg-gray-800",
                  isSelected && "bg-[#F1FAF8] text-green dark:bg-teal-950/40",
                )}
                onClick={() => {
                  closeEnvironmentOptions();
                  if (environment.value !== value) {
                    onChange(environment.value as AdminCertificatesKpisEnvironment);
                  }
                }}
              >
                {environment.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default memo(CertificatesKpisEnvironmentSelector);
